import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ApiClientError } from "./api/client";
import { FleetApiError } from "./api/fleet";

const AUTH_ME_KEY = ["auth", "me"] as const;

/** A 401 from the data-api or the CC server means the session cookie is no longer valid. */
export function isUnauthorized(err: unknown): boolean {
  return (err instanceof ApiClientError || err instanceof FleetApiError) && err.status === 401;
}

/**
 * Sessions expire (fixed TTL) or get revoked while the SPA is open. The
 * cached `/auth/me` result would otherwise keep `RootGate` rendering the
 * shell while every request fails, so any 401 re-probes the session; when
 * that probe also fails, `RootGate` swaps in the login screen on the
 * current route and the user lands back on the same page after signing in.
 */
function onUnauthorized(err: unknown, queryKey?: readonly unknown[]): void {
  if (!isUnauthorized(err)) return;
  if (queryKey?.[0] === AUTH_ME_KEY[0] && queryKey[1] === AUTH_ME_KEY[1]) return;
  void queryClient.invalidateQueries({ queryKey: AUTH_ME_KEY }, { cancelRefetch: false });
}

/**
 * Per-endpoint policies are baked into the query keys + meta:
 *   - "live"     → 5s stale, refetch on focus
 *   - "history"  → 60s stale, no refetch on focus
 *   - "config"   → 0s stale (always fresh)
 *   - "stats"    → 30s stale
 *
 * Retries: 4xx never retries, 5xx up to 2 retries with exponential delay.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (err, query) => onUnauthorized(err, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (err) => onUnauthorized(err) }),
  defaultOptions: {
    queries: {
      retry: (failureCount, err) => {
        if (err instanceof ApiClientError) {
          if (err.status >= 400 && err.status < 500) return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
    mutations: { retry: 0 },
  },
});

export const QK = {
  health: ["health"] as const,
  live: {
    status: ["live", "status"] as const,
    window: ["live", "window"] as const,
  },
  windows: (q: Record<string, unknown> = {}) => ["windows", q] as const,
  windowDetail: (slug: string) => ["window", slug] as const,
  windowTicks: (slug: string) => ["window", slug, "ticks"] as const,
  windowChecks: (slug: string) => ["window", slug, "checks"] as const,
  orders: (q: Record<string, unknown> = {}) => ["orders", q] as const,
  orderDetail: (id: string) => ["order", id] as const,
  stats: (q: Record<string, unknown> = {}) => ["stats", q] as const,
  configActive: ["config", "active"] as const,
  syncStatus: ["sync", "status"] as const,
};
